/**
 * AgentNodeBottomBar -- Floating refinement panel for agent nodes
 *
 * Provides text input, style selection, iteration count,
 * reference image picking, and a generate button.
 * Springs down with fade-in animation when node is selected.
 * Uses inverse zoom scaling to maintain readable size when zoomed out.
 */

import { memo, useState, useEffect } from 'react'
import { useViewport } from 'reactflow'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../../../lib/utils'
import { ImagePickerModal, type SelectedImage } from '../../../chat/ImagePickerModal'
import { useReadOnly } from '../../../../contexts/ReadOnlyContext'

interface StyleOption {
  id: string
  label: string
}

interface AgentNodeBottomBarProps {
  isVisible: boolean
  feedbackText: string
  onFeedbackTextChange: (text: string) => void
  onSubmit: () => void
  isRunning: boolean
  placeholder?: string
  entityName?: string
  originalPrompt?: string
  showIterations?: boolean
  styles?: StyleOption[]
  selectedStyle?: string
  onStyleChange?: (styleId: string) => void
  onAddImages?: (images: SelectedImage[]) => void
  projectId?: string
}

export const AgentNodeBottomBar = memo(function AgentNodeBottomBar({
  isVisible,
  feedbackText,
  onFeedbackTextChange,
  onSubmit,
  isRunning,
  placeholder = "Describe the changes you'd like to make...",
  entityName,
  originalPrompt,
  showIterations = false,
  styles,
  selectedStyle,
  onStyleChange,
  onAddImages,
  projectId,
}: AgentNodeBottomBarProps) {
  const readOnly = useReadOnly()
  const { zoom } = useViewport()
  const [iterations, setIterations] = useState(1)
  const [showIterationMenu, setShowIterationMenu] = useState(false)
  const [showStyleMenu, setShowStyleMenu] = useState(false)
  const [showImagePicker, setShowImagePicker] = useState(false)

  // Reset dropdown states when panel closes
  useEffect(() => {
    if (!isVisible) {
      setShowStyleMenu(false)
      setShowIterationMenu(false)
      setShowImagePicker(false)
    }
  }, [isVisible])

  const handleImagesConfirm = (images: SelectedImage[]) => {
    onAddImages?.(images)
    setShowImagePicker(false)
  }

  const selectedStyleLabel = styles?.find((s) => s.id === selectedStyle)?.label || 'Style'
  const scale = Math.min(Math.max(1 / zoom, 0.5), 2)

  return (
    <>
      {projectId && (
        <ImagePickerModal
          isOpen={showImagePicker}
          onClose={() => setShowImagePicker(false)}
          projectId={projectId}
          onConfirm={handleImagesConfirm}
        />
      )}

      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale, x: '-50%' }}
            animate={{ opacity: 1, y: 0, scale, x: '-50%' }}
            exit={{ opacity: 0, y: -10, scale, x: '-50%' }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 25,
              opacity: { duration: 0.15 },
            }}
            className="absolute z-50 nopan nodrag"
            style={{
              top: '100%',
              left: '50%',
              marginTop: 8,
              transformOrigin: 'top center',
              width: 'calc(100% + 120px)',
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn("bg-surface-1 border border-border rounded-xl shadow-2xl overflow-hidden", readOnly && "pointer-events-none select-text opacity-70")}>
              {/* Title */}
              <div className="px-4 py-2.5 border-b border-border-subtle">
                <h4 className="text-sm font-medium text-foreground">
                  {entityName ? `Refine ${entityName}` : 'Refine'}
                </h4>
              </div>

              {/* Original Prompt Reference */}
              {originalPrompt && (
                <div className="px-4 py-2.5 border-b border-border-subtle">
                  <span className="text-xxs font-medium text-text-muted uppercase tracking-wide">
                    Original prompt
                  </span>
                  <p className="text-xs text-text-secondary leading-relaxed line-clamp-2 mt-1">
                    {originalPrompt}
                  </p>
                </div>
              )}

              {/* Text Input Area */}
              <div className="p-3">
                <textarea
                  value={feedbackText}
                  onChange={(e) => onFeedbackTextChange(e.target.value)}
                  placeholder={placeholder}
                  maxLength={1000}
                  rows={2}
                  className={cn(
                    'w-full text-sm text-foreground bg-transparent',
                    'border-0 px-1 py-1',
                    'resize-none focus:outline-none',
                    'placeholder:text-text-muted nowheel',
                    'leading-relaxed'
                  )}
                  onKeyDown={(e) => {
                    if (
                      e.key === 'Enter' &&
                      (e.metaKey || e.ctrlKey) &&
                      feedbackText.trim()
                    ) {
                      e.preventDefault()
                      onSubmit()
                    }
                  }}
                />
              </div>

              {/* Bottom Action Bar */}
              <div className="px-3 py-2 border-t border-border-subtle flex items-center justify-between">
                {/* Left side: Add Image + Style */}
                <div className="flex items-center gap-1">
                  {/* Add Image Button */}
                  <button
                    onClick={() => setShowImagePicker(true)}
                    disabled={!projectId}
                    className="h-7 px-2.5 flex items-center gap-1.5 rounded-full text-xs text-text-secondary hover:text-foreground hover:bg-surface-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      projectId
                        ? 'Add reference image'
                        : 'Add reference image (unavailable)'
                    }
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span>Image</span>
                  </button>

                  {/* Divider */}
                  <div className="w-px h-4 bg-border-subtle mx-1" />

                  {/* Style Dropdown */}
                  {styles && styles.length > 0 && onStyleChange && (
                    <div className="relative">
                      <button
                        onClick={() => setShowStyleMenu(!showStyleMenu)}
                        className="h-7 px-2.5 flex items-center gap-1.5 rounded-full text-xs text-text-secondary hover:text-foreground hover:bg-surface-2 transition-all"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.39m3.421 3.415a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.764m3.42 3.42a6.776 6.776 0 00-3.42-3.42"
                          />
                        </svg>
                        <span>{selectedStyleLabel}</span>
                        <svg
                          className="w-2.5 h-2.5 text-text-muted"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      <AnimatePresence>
                        {showStyleMenu && (
                          <motion.div
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 4 }}
                            className="absolute bottom-full left-0 mb-1 bg-surface-1 border border-border rounded-lg shadow-xl overflow-hidden min-w-[120px] z-10"
                          >
                            {styles.map((style) => (
                              <button
                                key={style.id}
                                onClick={() => {
                                  onStyleChange(style.id)
                                  setShowStyleMenu(false)
                                }}
                                className={cn(
                                  'w-full px-3 py-2 text-xs text-left transition-colors',
                                  selectedStyle === style.id
                                    ? 'bg-surface-2 text-foreground'
                                    : 'text-text-secondary hover:bg-surface-2 hover:text-foreground'
                                )}
                              >
                                {style.label}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>

                {/* Right side: Quantity + Generate */}
                <div className="flex items-center gap-2">
                  {/* Iteration Selector */}
                  {showIterations && (
                    <div className="relative">
                      <button
                        onClick={() =>
                          setShowIterationMenu(!showIterationMenu)
                        }
                        className="h-7 px-2.5 flex items-center gap-1 rounded-full text-xs text-text-secondary hover:text-foreground hover:bg-surface-2 font-medium transition-all"
                      >
                        {iterations}x
                        <svg
                          className="w-2.5 h-2.5 text-text-muted"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      <AnimatePresence>
                        {showIterationMenu && (
                          <motion.div
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 4 }}
                            className="absolute bottom-full right-0 mb-1 bg-surface-1 border border-border rounded-lg shadow-xl overflow-hidden min-w-[50px] z-10"
                          >
                            {[1, 2, 3, 4].map((n) => (
                              <button
                                key={n}
                                onClick={() => {
                                  setIterations(n)
                                  setShowIterationMenu(false)
                                }}
                                className={cn(
                                  'w-full px-3 py-2 text-xs text-left transition-colors',
                                  iterations === n
                                    ? 'bg-surface-2 text-foreground'
                                    : 'text-text-secondary hover:bg-surface-2 hover:text-foreground'
                                )}
                              >
                                {n}x
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* Generate Button */}
                  <button
                    onClick={onSubmit}
                    disabled={isRunning}
                    className={cn(
                      'h-8 px-4 flex items-center justify-center gap-2 rounded-full text-sm font-medium transition-all',
                      isRunning
                        ? 'bg-surface-2 text-text-muted cursor-not-allowed'
                        : 'bg-foreground hover:bg-foreground/90 text-background'
                    )}
                  >
                    {isRunning ? (
                      <>
                        <svg
                          className="w-3.5 h-3.5 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18"
                          />
                        </svg>
                        <span>Generate</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
})

AgentNodeBottomBar.displayName = 'AgentNodeBottomBar'
